import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Sun, Coffee, Utensils, Moon as MoonIcon, Cookie, ChevronLeft, ChevronRight,
  RefreshCw, Download, Printer, Calendar, Check, ArrowLeft, Sparkles,
  Clock, Flame, Apple
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// 14-Day Rotating Meal Plan based on document specifications
const mealPlanDatabase = {
  week1: [
    {
      day: 'Monday',
      breakfast: { name: 'Scrambled Eggs + Toast + Fruit', prep: '10 min', protein: 20, carbs: 35, calories: 350 },
      lunch: { name: 'Turkey Wrap + Veggies', prep: 'Pack ahead', protein: 25, carbs: 30, calories: 380 },
      dinner: { name: 'Grilled Chicken + Rice + Broccoli', prep: '25 min', protein: 40, carbs: 45, calories: 520, batchCook: true },
      snacks: [
        { name: 'Greek Yogurt + Berries', time: 'Morning' },
        { name: 'Apple + Peanut Butter', time: 'Pre-Practice' },
        { name: 'Chocolate Milk', time: 'Post-Workout' }
      ]
    },
    {
      day: 'Tuesday',
      breakfast: { name: 'Smoothie (Fruit + Protein + Greens)', prep: '5 min', protein: 25, carbs: 40, calories: 320 },
      lunch: { name: 'Chicken Salad Bowl', prep: 'Leftovers', protein: 30, carbs: 25, calories: 350 },
      dinner: { name: 'Salmon + Quinoa + Green Beans', prep: '20 min', protein: 35, carbs: 40, calories: 480 },
      snacks: [
        { name: 'Trail Mix', time: 'Morning' },
        { name: 'Banana + Almond Butter', time: 'Pre-Practice' },
        { name: 'Protein Shake', time: 'Post-Workout' }
      ]
    },
    {
      day: 'Wednesday',
      breakfast: { name: 'Overnight Oats + Berries', prep: 'Made night before', protein: 15, carbs: 50, calories: 340 },
      lunch: { name: 'Turkey Sandwich + Fruit', prep: 'Pack ahead', protein: 22, carbs: 40, calories: 400 },
      dinner: { name: 'Taco Bowl (Beef + Beans + Rice + Veggies)', prep: '20 min', protein: 38, carbs: 50, calories: 550, batchCook: true },
      snacks: [
        { name: 'Cheese Sticks', time: 'Morning' },
        { name: 'Granola Bar', time: 'Pre-Practice' },
        { name: 'Fruit Cup', time: 'Post-Workout' }
      ]
    },
    {
      day: 'Thursday',
      breakfast: { name: 'Egg Wrap + Fruit', prep: '10 min', protein: 22, carbs: 35, calories: 360 },
      lunch: { name: 'Taco Bowl Leftovers', prep: 'Reheat', protein: 35, carbs: 45, calories: 500 },
      dinner: { name: 'Shrimp Stir-Fry + Veggies + Rice', prep: '15 min', protein: 30, carbs: 45, calories: 450 },
      snacks: [
        { name: 'Veggies + Hummus', time: 'Morning' },
        { name: 'Apple + String Cheese', time: 'Pre-Practice' },
        { name: 'Chocolate Milk', time: 'Post-Workout' }
      ]
    },
    {
      day: 'Friday',
      breakfast: { name: 'Pancakes (Protein Mix) + Fruit', prep: '15 min', protein: 20, carbs: 55, calories: 420 },
      lunch: { name: 'Wrap or Salad', prep: 'Pack ahead', protein: 25, carbs: 30, calories: 350 },
      dinner: { name: 'Chicken Wings + Cabbage + Rice', prep: '25 min', protein: 35, carbs: 40, calories: 500 },
      snacks: [
        { name: 'Yogurt Parfait', time: 'Morning' },
        { name: 'Trail Mix', time: 'Pre-Practice' },
        { name: 'Protein Bar', time: 'Post-Workout' }
      ]
    },
    {
      day: 'Saturday',
      breakfast: { name: 'Big Breakfast: Eggs + Bacon + Toast + Fruit', prep: '20 min', protein: 30, carbs: 45, calories: 500 },
      lunch: { name: 'Grilled Chicken Salad', prep: '10 min', protein: 35, carbs: 20, calories: 380 },
      dinner: { name: 'Air-Fried Salmon + Beans + Salad', prep: '20 min', protein: 38, carbs: 35, calories: 460 },
      snacks: [
        { name: 'Smoothie Bowl', time: 'Morning' },
        { name: 'Banana', time: 'Pre-Game' },
        { name: 'Recovery Shake', time: 'Post-Game' }
      ]
    },
    {
      day: 'Sunday',
      breakfast: { name: 'French Toast + Turkey Sausage', prep: '15 min', protein: 25, carbs: 50, calories: 450 },
      lunch: { name: 'Leftovers or Light Salad', prep: 'Varies', protein: 25, carbs: 30, calories: 350 },
      dinner: { name: 'Beef Tacos + Beans + Rice', prep: '20 min', protein: 40, carbs: 50, calories: 550, batchCook: true },
      snacks: [
        { name: 'Fruit Salad', time: 'Anytime' },
        { name: 'Popcorn', time: 'Movie Night' }
      ],
      prepDay: true
    }
  ],
  week2: [
    {
      day: 'Monday',
      breakfast: { name: 'Greek Yogurt + Granola + Berries', prep: '5 min', protein: 20, carbs: 45, calories: 350 },
      lunch: { name: 'Chicken Rice Bowl', prep: 'Leftovers', protein: 30, carbs: 40, calories: 420 },
      dinner: { name: 'Grilled Chicken + Green Beans + Rice', prep: '25 min', protein: 40, carbs: 45, calories: 500, batchCook: true },
      snacks: [
        { name: 'Apple + Cheese', time: 'Morning' },
        { name: 'Energy Bites', time: 'Pre-Practice' },
        { name: 'Chocolate Milk', time: 'Post-Workout' }
      ]
    },
    {
      day: 'Tuesday',
      breakfast: { name: 'Egg Muffins + Fruit', prep: 'Made ahead', protein: 22, carbs: 25, calories: 300 },
      lunch: { name: 'Turkey Wrap + Chips', prep: 'Pack ahead', protein: 25, carbs: 40, calories: 400 },
      dinner: { name: 'Shrimp Alfredo + Broccoli', prep: '20 min', protein: 32, carbs: 50, calories: 520 },
      snacks: [
        { name: 'Yogurt Tube', time: 'Morning' },
        { name: 'Banana + Peanut Butter', time: 'Pre-Practice' },
        { name: 'Protein Shake', time: 'Post-Workout' }
      ]
    },
    {
      day: 'Wednesday',
      breakfast: { name: 'Smoothie Bowl', prep: '10 min', protein: 20, carbs: 55, calories: 380 },
      lunch: { name: 'Pasta Salad + Chicken', prep: 'Made ahead', protein: 28, carbs: 45, calories: 450 },
      dinner: { name: 'Chicken + Cabbage + Rice', prep: '20 min', protein: 38, carbs: 40, calories: 480, batchCook: true },
      snacks: [
        { name: 'Cheese + Crackers', time: 'Morning' },
        { name: 'Orange Slices', time: 'Pre-Practice' },
        { name: 'Recovery Bar', time: 'Post-Workout' }
      ],
      prepDay: true
    },
    {
      day: 'Thursday',
      breakfast: { name: 'Avocado Toast + Eggs', prep: '10 min', protein: 18, carbs: 30, calories: 340 },
      lunch: { name: 'Chicken Cabbage Bowl', prep: 'Leftovers', protein: 35, carbs: 35, calories: 420 },
      dinner: { name: 'Salmon + Quinoa + Salad', prep: '20 min', protein: 35, carbs: 40, calories: 470 },
      snacks: [
        { name: 'Trail Mix', time: 'Morning' },
        { name: 'Grapes', time: 'Pre-Practice' },
        { name: 'Chocolate Milk', time: 'Post-Workout' }
      ]
    },
    {
      day: 'Friday',
      breakfast: { name: 'Waffles + Turkey Bacon + Fruit', prep: '15 min', protein: 22, carbs: 50, calories: 420 },
      lunch: { name: 'Hearty Sandwich + Veggies', prep: 'Pack ahead', protein: 28, carbs: 40, calories: 420 },
      dinner: { name: 'Shrimp Alfredo + Veggies', prep: '18 min', protein: 30, carbs: 48, calories: 500 },
      snacks: [
        { name: 'Yogurt + Honey', time: 'Morning' },
        { name: 'Banana', time: 'Pre-Practice' },
        { name: 'Protein Shake', time: 'Post-Workout' }
      ]
    },
    {
      day: 'Saturday',
      breakfast: { name: 'Breakfast Burrito', prep: '15 min', protein: 28, carbs: 45, calories: 480 },
      lunch: { name: 'Salad Bowl + Grilled Chicken', prep: '10 min', protein: 35, carbs: 25, calories: 400 },
      dinner: { name: 'Taco Bowl', prep: '15 min', protein: 38, carbs: 50, calories: 540 },
      snacks: [
        { name: 'Fruit Smoothie', time: 'Pre-Game' },
        { name: 'Energy Gel', time: 'Halftime' },
        { name: 'Recovery Shake', time: 'Post-Game' }
      ]
    },
    {
      day: 'Sunday',
      breakfast: { name: 'Eggs Benedict (Healthy Version)', prep: '20 min', protein: 25, carbs: 35, calories: 400 },
      lunch: { name: 'Leftovers + Fresh Salad', prep: 'Varies', protein: 28, carbs: 30, calories: 380 },
      dinner: { name: 'Grilled Steak + Sweet Potato + Asparagus', prep: '25 min', protein: 42, carbs: 45, calories: 550, batchCook: true },
      snacks: [
        { name: 'Fresh Fruit', time: 'Anytime' },
        { name: 'Popcorn', time: 'Movie Night' }
      ],
      prepDay: true
    }
  ]
};

// Meal options for swapping
const mealSwapOptions = {
  breakfast: [
    'Scrambled Eggs + Toast + Fruit',
    'Smoothie (Fruit + Protein + Greens)',
    'Overnight Oats + Berries',
    'Greek Yogurt + Granola + Berries',
    'Egg Wrap + Fruit',
    'Pancakes (Protein Mix) + Fruit',
    'Avocado Toast + Eggs',
    'Egg Muffins + Fruit',
    'French Toast + Turkey Sausage',
    'Waffles + Turkey Bacon + Fruit'
  ],
  lunch: [
    'Turkey Wrap + Veggies',
    'Chicken Salad Bowl',
    'Turkey Sandwich + Fruit',
    'Pasta Salad + Chicken',
    'Leftovers',
    'Grilled Chicken Salad',
    'Wrap or Salad',
    'Chicken Rice Bowl'
  ],
  dinner: [
    'Grilled Chicken + Rice + Broccoli',
    'Salmon + Quinoa + Green Beans',
    'Taco Bowl (Beef + Beans + Rice)',
    'Shrimp Stir-Fry + Veggies + Rice',
    'Shrimp Alfredo + Broccoli',
    'Chicken Wings + Cabbage + Rice',
    'Air-Fried Salmon + Beans + Salad',
    'Beef Tacos + Beans + Rice',
    'Grilled Steak + Sweet Potato + Asparagus'
  ],
  snacks: [
    'Greek Yogurt + Berries',
    'Apple + Peanut Butter',
    'Trail Mix',
    'Banana + Almond Butter',
    'Cheese Sticks',
    'Granola Bar',
    'Veggies + Hummus',
    'Protein Bar',
    'Chocolate Milk',
    'Fruit Cup'
  ]
};

const WeeklyMealPlanPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentWeek, setCurrentWeek] = useState(1);
  const [selectedDay, setSelectedDay] = useState(null);
  const [customPlan, setCustomPlan] = useState(null);
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null);

  const weekData = currentWeek === 1 ? mealPlanDatabase.week1 : mealPlanDatabase.week2;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const todayPlan = weekData.find(d => d.day === today) || weekData[0];

  useEffect(() => {
    // Load any custom modifications
    const saved = localStorage.getItem('aura_custom_meal_plan');
    if (saved) {
      setCustomPlan(JSON.parse(saved));
    }
  }, []);

  const getMeal = (day, mealType) => {
    if (customPlan?.[`${currentWeek}_${day}_${mealType}`]) {
      return { name: customPlan[`${currentWeek}_${day}_${mealType}`] };
    }
    const dayData = weekData.find(d => d.day === day);
    return dayData?.[mealType];
  };

  const handleSwap = (day, mealType) => {
    setSwapTarget({ day, mealType });
    setShowSwapDialog(true);
  };

  const confirmSwap = (newMeal) => {
    const updated = {
      ...customPlan,
      [`${currentWeek}_${swapTarget.day}_${swapTarget.mealType}`]: newMeal
    };
    setCustomPlan(updated);
    localStorage.setItem('aura_custom_meal_plan', JSON.stringify(updated));
    setShowSwapDialog(false);
    toast.success('Meal swapped!');
  };

  const resetPlan = () => {
    setCustomPlan(null);
    localStorage.removeItem('aura_custom_meal_plan');
    toast.success('Meal plan reset to default');
  };

  const MealCard = ({ meal, type, day, icon: Icon, color }) => (
    <div 
      className={`p-4 rounded-xl ${color} border border-white/10 hover:border-white/20 transition-all cursor-pointer group`}
      onClick={() => handleSwap(day, type)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-white/70" />
          <span className="text-xs text-white/50 uppercase tracking-wider">{type}</span>
        </div>
        <button className="opacity-0 group-hover:opacity-100 text-xs text-aura-primary transition-opacity">
          Swap
        </button>
      </div>
      <p className="font-medium text-white text-sm">{meal?.name}</p>
      {meal?.prep && (
        <div className="flex items-center gap-1 mt-2 text-xs text-white/50">
          <Clock className="w-3 h-3" />
          {meal.prep}
        </div>
      )}
      {meal?.protein && (
        <div className="flex gap-3 mt-2 text-xs">
          <span className="text-red-300">{meal.protein}g protein</span>
          <span className="text-amber-300">{meal.carbs}g carbs</span>
          <span className="text-green-300">{meal.calories} cal</span>
        </div>
      )}
      {meal?.batchCook && (
        <Badge className="mt-2 bg-purple-500/20 text-purple-300 border-0 text-xs">
          Batch Cook
        </Badge>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 text-aura-text-muted hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 bg-green-500 rounded-sm flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">14-DAY MEAL PLAN</span>
          </div>
          <Button 
            onClick={resetPlan}
            variant="ghost"
            className="text-aura-text-muted hover:text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
        </div>
      </header>

      <main className="px-4 py-6 max-w-5xl mx-auto space-y-6">
        {/* Week Selector */}
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="ghost"
            onClick={() => setCurrentWeek(1)}
            className={currentWeek === 1 ? 'text-white' : 'text-aura-text-muted'}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
              Week {currentWeek}
            </h1>
            <p className="text-sm text-aura-text-muted">Tap any meal to swap</p>
          </div>
          <Button
            variant="ghost"
            onClick={() => setCurrentWeek(2)}
            className={currentWeek === 2 ? 'text-white' : 'text-aura-text-muted'}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Today's Plan Highlight */}
        <Card className="bg-gradient-to-r from-aura-primary/20 to-green-500/10 border-aura-primary/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-aura-primary" />
                Today's Plan - {today}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-4 gap-3">
              <MealCard meal={getMeal(today, 'breakfast')} type="breakfast" day={today} icon={Coffee} color="bg-amber-500/10" />
              <MealCard meal={getMeal(today, 'lunch')} type="lunch" day={today} icon={Sun} color="bg-yellow-500/10" />
              <MealCard meal={getMeal(today, 'dinner')} type="dinner" day={today} icon={MoonIcon} color="bg-blue-500/10" />
              <div className="p-4 rounded-xl bg-green-500/10 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Cookie className="w-5 h-5 text-white/70" />
                  <span className="text-xs text-white/50 uppercase tracking-wider">Snacks</span>
                </div>
                {todayPlan?.snacks?.map((snack, idx) => (
                  <div key={idx} className="text-sm text-white/80 mb-1">
                    • {snack.name} <span className="text-white/40">({snack.time})</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Full Week View */}
        <Tabs defaultValue="grid" className="w-full">
          <TabsList className="bg-aura-surface border border-white/10">
            <TabsTrigger value="grid">Grid View</TabsTrigger>
            <TabsTrigger value="list">List View</TabsTrigger>
          </TabsList>

          <TabsContent value="grid" className="mt-4">
            <div className="grid gap-4">
              {weekData.map((day, idx) => (
                <Card key={idx} className={`bg-aura-surface border-white/10 ${day.prepDay ? 'ring-2 ring-purple-500/30' : ''}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading font-bold text-white uppercase tracking-tight flex items-center gap-2">
                        {day.day}
                        {day.day === today && <Badge className="bg-aura-primary text-white border-0 text-xs">Today</Badge>}
                        {day.prepDay && <Badge className="bg-purple-500/20 text-purple-300 border-0 text-xs">Prep Day</Badge>}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-4 gap-3">
                      <MealCard meal={getMeal(day.day, 'breakfast')} type="breakfast" day={day.day} icon={Coffee} color="bg-amber-500/10" />
                      <MealCard meal={getMeal(day.day, 'lunch')} type="lunch" day={day.day} icon={Sun} color="bg-yellow-500/10" />
                      <MealCard meal={getMeal(day.day, 'dinner')} type="dinner" day={day.day} icon={MoonIcon} color="bg-blue-500/10" />
                      <div className="p-4 rounded-xl bg-green-500/10 border border-white/10">
                        <div className="flex items-center gap-2 mb-2">
                          <Cookie className="w-5 h-5 text-white/70" />
                          <span className="text-xs text-white/50 uppercase">Snacks</span>
                        </div>
                        {day.snacks?.slice(0, 3).map((snack, sidx) => (
                          <div key={sidx} className="text-xs text-white/70 mb-1 truncate">
                            • {snack.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="list" className="mt-4 space-y-4">
            {['breakfast', 'lunch', 'dinner'].map(mealType => (
              <Card key={mealType} className="bg-aura-surface border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading font-bold text-white uppercase tracking-tight capitalize flex items-center gap-2">
                    {mealType === 'breakfast' && <Coffee className="w-5 h-5 text-amber-400" />}
                    {mealType === 'lunch' && <Sun className="w-5 h-5 text-yellow-400" />}
                    {mealType === 'dinner' && <MoonIcon className="w-5 h-5 text-blue-400" />}
                    {mealType} This Week
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {weekData.map((day, idx) => (
                      <div 
                        key={idx}
                        onClick={() => handleSwap(day.day, mealType)}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-aura-text-muted w-20">{day.day}</span>
                          <span className="text-white text-sm">{getMeal(day.day, mealType)?.name}</span>
                        </div>
                        <span className="text-xs text-aura-primary">Swap</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>

        {/* Prep Day Tips */}
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Utensils className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-white uppercase tracking-tight">Prep Day Tips (Sunday & Wednesday)</h3>
                <ul className="text-sm text-aura-text-muted mt-2 space-y-1">
                  <li>• Batch cook proteins: chicken, ground beef, or salmon</li>
                  <li>• Cook large batches of rice or quinoa</li>
                  <li>• Wash and cut veggies for the week</li>
                  <li>• Pack snacks in grab-and-go bags</li>
                  <li>• Prep overnight oats or egg muffins for quick breakfasts</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Swap Dialog */}
      <Dialog open={showSwapDialog} onOpenChange={setShowSwapDialog}>
        <DialogContent className="bg-aura-surface border-white/10 text-white max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Swap {swapTarget?.mealType} for {swapTarget?.day}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-4">
            {swapTarget && mealSwapOptions[swapTarget.mealType]?.map((option, idx) => (
              <button
                key={idx}
                onClick={() => confirmSwap(option)}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <span className="text-white">{option}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WeeklyMealPlanPage;
